import { memo } from 'react';
import { Handle, Position } from '@xyflow/react';
import CategoryBadge from '../../shared/CategoryBadge';
import EvidenceLevelBadge from '../../shared/EvidenceLevelBadge';
import RegulatoryChip from '../../shared/RegulatoryChip';
import './nodeStyles.css';

function normalizeRegulatory(reg) {
  if (!reg) return [];
  return Array.isArray(reg) ? reg : [reg];
}

// Shape and text carry the node type as well as colour, so the graph stays
// readable without colour vision.
const TYPE_MARKS = {
  diagnosis: '◆',
  workup: '▣',
  decision: '?',
  treatment: '✚',
  response: '◑',
  surveillance: '◔',
  reference: '→',
};

function BaseNode({ data, className }) {
  const isBeyondNccn = data.status === 'beyond-nccn';
  const isWithdrawn = data.status === 'withdrawn';
  const regArray = normalizeRegulatory(data.regulatory);

  const describedType = data.type ? data.type.replace(/-/g, ' ') : 'step';
  const ariaLabel = [
    `${describedType}: ${data.label}`,
    data.category ? `NCCN category ${data.category}` : '',
    isWithdrawn ? 'withdrawn' : '',
    isBeyondNccn ? 'beyond NCCN' : '',
    'activate for details',
  ]
    .filter(Boolean)
    .join('. ');

  // React Flow owns the click handler on the wrapper, so a synthetic click from
  // the keyboard bubbles to exactly the same place a mouse click would.
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.currentTarget.click();
    }
  };

  const classes = [
    'custom-node',
    className,
    isBeyondNccn ? 'node-beyond-nccn' : '',
    isWithdrawn ? 'node-withdrawn' : '',
  ].filter(Boolean).join(' ');

  const hasFooter =
    data.category ||
    data.evidenceLevel ||
    regArray.length > 0 ||
    (data.footnoteRefs && data.footnoteRefs.length > 0);

  return (
    <div
      className={classes}
      role="button"
      tabIndex={0}
      aria-label={ariaLabel}
      onKeyDown={handleKeyDown}
    >
      {isBeyondNccn && <span className="beyond-nccn-pill">Beyond NCCN</span>}
      {isWithdrawn && <span className="withdrawn-pill">Withdrawn</span>}
      <Handle type="target" position={Position.Left} />
      <div className="node-label">
        <span className="node-type-mark" aria-hidden="true">
          {TYPE_MARKS[data.type] || '•'}
        </span>
        {data.label}
      </div>
      {data.options && data.options.length > 0 && (
        <div className="node-options">
          {data.options.map((opt, i) => (
            <span key={i}>{opt.label}</span>
          ))}
        </div>
      )}
      {hasFooter && (
        <div className="node-footer">
          {data.category && <CategoryBadge category={data.category} />}
          {data.evidenceLevel && <EvidenceLevelBadge level={data.evidenceLevel} />}
          {regArray.map((r, i) => (
            <RegulatoryChip key={i} regulatory={r} compact />
          ))}
          {data.footnoteRefs && data.footnoteRefs.length > 0 && (
            <span className="node-footnotes">
              [{data.footnoteRefs.join(', ')}]
            </span>
          )}
        </div>
      )}
      <Handle type="source" position={Position.Right} />
    </div>
  );
}

export default memo(BaseNode);
